import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { getRoleById } from '@/services/roleService';
import { useTranslation } from '@/hooks/useTranslation';
import { toTitleCase } from '@utils/textCase';
import { useAuthStore } from '@/stores/authStore';
import PermissionPicker from './PermissionPicker';
import styles from './RoleModals.module.scss';

// ============================================
// TYPES
// ============================================

interface ViewRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  roleId: string | null;
  onEdit: () => void;
  onDelete: () => void;
  onAudit?: () => void;
}

// ============================================
// COMPONENT
// ============================================

function ViewRoleModal({
  isOpen,
  onClose,
  roleId,
  onEdit,
  onDelete,
  onAudit,
}: ViewRoleModalProps) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data: role, isLoading } = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => getRoleById(roleId!),
    enabled: isOpen && !!roleId,
  });

  const selectedIds = useMemo(
    () => new Set(role?.permissions.map((p) => p.id) ?? []),
    [role],
  );

  const isSystem = role?.roleType === 'SYSTEM';
  const isSuperAdmin = role?.roleType === 'SUPER_ADMIN';
  const canEdit = !isSystem && !isSuperAdmin && hasPermission('ROLES_UPDATE');
  const canDelete = !isSystem && !isSuperAdmin && hasPermission('ROLES_DELETE');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={role?.name ?? t('K_MODAL_VIEW_ROLE', 'Role Details')}
      size="lg"
      footer={
        <div className={styles.viewFooter}>
          <div>
            {canDelete && (
              <Button variant="danger" size="sm" onClick={onDelete} disabled={isLoading}>
                {t('K_DELETE', 'Delete')}
              </Button>
            )}
          </div>
          <div className={styles.viewFooterRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('K_CLOSE', 'Close')}
            </Button>
            {onAudit && hasPermission('AUDIT_READ') && (
              <Button variant="outline" size="sm" onClick={onAudit} disabled={isLoading}>
                {t('K_AUDIT_TITLE', 'Audit trail')}
              </Button>
            )}
            {canEdit && (
              <Button variant="filled" size="sm" onClick={onEdit} disabled={isLoading}>
                {t('K_EDIT', 'Edit')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {isLoading ? (
        <div className={styles.loading}>{t('K_LOADING', 'Loading...')}</div>
      ) : role ? (
        <div className={styles.viewBody}>
          <div className={styles.fieldGrid}>
            <div className={styles.fieldItem}>
              <span className={styles.fieldLabel}>{t('K_COLUMN_ROLE_NAME', 'Name')}</span>
              <span className={styles.fieldValue}>{role.name}</span>
            </div>
            <div className={styles.fieldItem}>
              <span className={styles.fieldLabel}>{t('K_COLUMN_ROLE_TYPE', 'Type')}</span>
              <span className={`${styles.typeBadge} ${styles[`typeBadge${role.roleType}`]}`}>
                {toTitleCase(role.roleType)}
              </span>
            </div>
            {role.description && (
              <div className={`${styles.fieldItem} ${styles.fieldItemFull}`}>
                <span className={styles.fieldLabel}>{t('K_COLUMN_DESCRIPTION', 'Description')}</span>
                <span className={styles.fieldValue}>{role.description}</span>
              </div>
            )}
          </div>

          <div className={styles.permissionsSection}>
            <h3 className={styles.sectionTitle}>{t('K_STEP_PERMISSIONS', 'Permissions')}</h3>
            <PermissionPicker
              selectedIds={selectedIds}
              onChange={() => {}}
              readOnly
              enabled={isOpen}
            />
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

export default ViewRoleModal;
